import { FIELD } from "./config.js";
import type { SpawnSpec } from "./formation.js";
import {
  attackingSeatIndex,
  FORMATION_2V2,
  FORMATION_3V3,
  FORMATION_4V4,
  FORMATION_442,
  FORMATION_5V5,
  FORMATION_6V6,
  FORMATION_7V7,
  FORMATION_8V8,
  FORMATION_9V9,
  FORMATION_10V10,
} from "./formation.js";

/**
 * Modos. El numero incluye al portero (futbol sala = 5, futbol 7 = 7, futbol 11
 * = 11), asi que un `NvN` tiene N-1 plazas jugables. El 1v1 se retiro: con
 * portero seria un concurso de penaltis.
 */
export type MatchModeId =
  | "2v2"
  | "3v3"
  | "4v4"
  | "5v5"
  | "6v6"
  | "7v7"
  | "8v8"
  | "9v9"
  | "10v10"
  | "11v11";

/** Perfil fisico y de pacing por modo de partido. */
export interface ModePhysics {
  /** Escala de velocidad maxima de jugador (1 = referencia arcade antigua). */
  speedScale: number;
  /** Escala de disparos y pases. */
  kickScale: number;
  /** Radio de jugador de campo. */
  playerRadius: number;
  /** Radio del balon. */
  ballRadius: number;
  /**
   * Alto de la boca de porteria como fraccion de `FIELD.GOAL_HEIGHT`.
   *
   * Los modos pequenos necesitan porteria pequena. El 1v1 no tiene portero, asi
   * que con la porteria completa era una galeria de tiro: 9 goles cada dos
   * minutos, uno cada trece segundos.
   */
  goalScale: number;
}

/** Perfil de agresividad ofensiva de la IA por modo. */
export interface ModeAiProfile {
  /** Preferencia de tiro (>1 = mas chutes). */
  shootBias: number;
  /** Preferencia de pase (<1 = menos pases). */
  passBias: number;
  /** Si false, la IA no pasa atras. */
  allowBackwardPass: boolean;
  /** Escala del umbral de tiro (<1 = dispara antes). */
  shotThresholdScale: number;
  /** Escala del rango de tiro. */
  shootRangeScale: number;
  /** Segundos de control antes de poder chutar. */
  minHold: number;
  /** Escala de la distancia a la que se planta para rematar. */
  standoffScale: number;
  /**
   * Hasta cuanto por detras del balon se le juega a un compañero LIBRE (a los
   * demas, 55 como siempre). Largo solo en campo grande: medido con
   * `pnpm ai:audit` (16 partidos por modo), en 11v11 el pase atras al central
   * libre subio el acierto de pase al 81 % (el de la Premier), 3,3 pases por
   * posesion y mas posesiones acabadas en tiro; en 5v5 y 7v7 volvia la
   * posesion esteril (un tercio de pases atras, 7v7 18 → 13 % de posesiones
   * con tiro).
   */
  backPassReach: number;
}

/**
 * Recorte global de ritmo, sobre todas las escalas de modo.
 *
 * Medido contra HaxBall el 2026-08-26, que es la referencia que pidio el
 * usuario. HaxBall clasico: campo de 740x340, ficha de radio 15, aceleracion
 * 0,1/tick con amortiguacion 0,96 — o sea velocidad terminal 2,4 u/tick, que a
 * 60 Hz son **144 u/s**. Cruzar su campo a tope: **5,14 s**.
 *
 * FYNTA antes de este recorte: 3v3 cruzaba en 5,19 s (practicamente igual) y
 * 11v11 en 7,27 s (ya mas lento). Donde SI iba mas rapido era en cuerpos por
 * segundo: 12,3 radios/s en 3v3 y 20 en 11v11, frente a los 9,6 de HaxBall.
 * Esa es la sensacion de "patinaje" — no la velocidad absoluta, sino cuanto se
 * mueve cada ficha respecto a su propio tamaño.
 *
 * -10% deja el 3v3 en 11,1 radios/s, mucho mas cerca de HaxBall, sin llevarse
 * por delante el ritmo. Es una sola constante a proposito: si hay que volver a
 * tocarlo, se toca aqui y no en diez sitios.
 */
const PACE = 0.9;

/**
 * Ritmo por modo (velocidad de jugador). Escala descendente hacia el 11v11
 * a mitad de gas: menos arcade, mas futbol.
 */
const BASE_SPEED = {
  "2v2": 0.78,
  "3v3": 0.7,
  "4v4": 0.65,
  "5v5": 0.6,
  "6v6": 0.575,
  "7v7": 0.55,
  "8v8": 0.535,
  "9v9": 0.52,
  "10v10": 0.51,
  "11v11": 0.5,
} as const;

const SPEED = Object.fromEntries(
  Object.entries(BASE_SPEED).map(([id, v]) => [id, v * PACE]),
) as Record<keyof typeof BASE_SPEED, number>;

/**
 * Alto de porteria por modo: la palanca para ajustar goles sin tocar la IA.
 *
 * Calibrado contra la conversion de tiro medida con `pnpm ai:audit`, buscando
 * el ~10% del futbol real. Los modos pequenos necesitan porteria GRANDE (1.05)
 * porque el portero cubre proporcionalmente mucho mas de una boca chica; los
 * grandes, pequena (0.45), porque hay mas gente estorbando el remate.
 *
 * **Subidas ~15% el 2026-08-26**, a la vez que el portero aprendio a lanzarse
 * (`GK.DIVE_*`). Las dos cosas van juntas y por eso se tocaron a la vez: la
 * porteria mas grande abre angulos, y la estirada los vuelve a cerrar, pero
 * ahora **se ve** como se cierran. Subir la boca sin dar al portero con que
 * llegar habria sido regalar goles; dar la estirada sin abrir la boca la
 * habria hecho invisible, porque no habria balones fuera de su alcance.
 */
const GOAL = {
  "2v2": 1.1,
  "3v3": 1.1,
  "4v4": 0.9,
  "5v5": 0.81,
  "6v6": 0.72,
  "7v7": 0.67,
  "8v8": 0.66,
  "9v9": 0.64,
  "10v10": 0.6,
  "11v11": 0.58,
} as const;

/** Golpeo un poco por debajo de la velocidad para que no vuele el balon. */
const KICK = {
  "2v2": 0.84,
  "3v3": 0.78,
  "4v4": 0.73,
  "5v5": 0.68,
  "6v6": 0.65,
  "7v7": 0.62,
  "8v8": 0.6,
  "9v9": 0.64,
  "10v10": 0.565,
  "11v11": 0.55,
} as const;

const AI_DUEL: ModeAiProfile = {
  shootBias: 1.35,
  passBias: 0.55,
  allowBackwardPass: false,
  shotThresholdScale: 0.55,
  shootRangeScale: 1.15,
  minHold: 0.14,
  backPassReach: 55,
  standoffScale: 0.58,
};

const AI_SMALL: ModeAiProfile = {
  shootBias: 1.08,
  passBias: 1.05,
  allowBackwardPass: false,
  shotThresholdScale: 0.7,
  shootRangeScale: 1.05,
  minHold: 0.18,
  backPassReach: 55,
  standoffScale: 0.7,
};

const AI_FUTSAL: ModeAiProfile = {
  shootBias: 0.95,
  passBias: 1.22,
  allowBackwardPass: true,
  shotThresholdScale: 0.88,
  shootRangeScale: 1,
  minHold: 0.24,
  backPassReach: 55,
  standoffScale: 0.88,
};

/** Ver `ModeAiProfile.backPassReach`. */
const AI_LONG_BACK_PASS = 240;

const AI_ELEVEN: ModeAiProfile = {
  shootBias: 1.05,
  passBias: 1.05,
  allowBackwardPass: true,
  shotThresholdScale: 0.88,
  shootRangeScale: 1.02,
  minHold: 0.3,
  backPassReach: AI_LONG_BACK_PASS,
  standoffScale: 0.95,
};

export interface MatchMode {
  id: MatchModeId;
  label: string;
  /** Subtitulo para el menu. */
  blurb: string;
  formation: readonly SpawnSpec[];
  physics: ModePhysics;
  /** Indice del jugador humano en el equipo local (`home-N`). */
  controlledIndex: number;
  /** Intervalo base de decision IA (s). Modos grandes = mas pausa tactica. */
  aiDecisionInterval: number;
  /** Mentality ofensiva de la IA en este modo. */
  ai: ModeAiProfile;
}

/** Modos visibles en el menu: la escalera completa del 2v2 al 11v11. */
export const MATCH_MODE_MENU_IDS: readonly MatchModeId[] = [
  "2v2",
  "3v3",
  "4v4",
  "5v5",
  "6v6",
  "7v7",
  "8v8",
  "9v9",
  "10v10",
  "11v11",
];

/** Todos los modos registrados. Hoy coincide con los del menu. */
export const MATCH_MODE_IDS: readonly MatchModeId[] = [...MATCH_MODE_MENU_IDS];

export const MATCH_MODES: Record<MatchModeId, MatchMode> = {
  "2v2": {
    id: "2v2",
    label: "2v2",
    blurb: "Duelo con porteros. Uno contra uno, y a la escuadra.",
    formation: FORMATION_2V2,
    physics: {
      speedScale: SPEED["2v2"],
      kickScale: KICK["2v2"],
      playerRadius: 25,
      ballRadius: 13,
      goalScale: GOAL["2v2"],
    },
    controlledIndex: attackingSeatIndex(FORMATION_2V2),
    aiDecisionInterval: 0.11,
    ai: AI_DUEL,
  },
  "3v3": {
    id: "3v3",
    label: "3v3",
    blurb: "Portero, un ancla y una punta. Paredes y espacio.",
    formation: FORMATION_3V3,
    physics: {
      speedScale: SPEED["3v3"],
      kickScale: KICK["3v3"],
      playerRadius: 25,
      ballRadius: 13,
      goalScale: GOAL["3v3"],
    },
    controlledIndex: attackingSeatIndex(FORMATION_3V3),
    aiDecisionInterval: 0.12,
    ai: AI_SMALL,
  },
  "4v4": {
    id: "4v4",
    label: "4v4",
    blurb: "Portero y 1-2. Ataque directo, defensa corta.",
    formation: FORMATION_4V4,
    physics: {
      speedScale: SPEED["4v4"],
      kickScale: KICK["4v4"],
      playerRadius: 20,
      ballRadius: 10,
      goalScale: GOAL["4v4"],
    },
    controlledIndex: attackingSeatIndex(FORMATION_4V4),
    aiDecisionInterval: 0.13,
    ai: AI_SMALL,
  },
  "5v5": {
    id: "5v5",
    label: "5v5",
    blurb: "Futbol sala exacto: diamante 1-2-1 con portero.",
    formation: FORMATION_5V5,
    physics: {
      speedScale: SPEED["5v5"],
      kickScale: KICK["5v5"],
      playerRadius: 16,
      ballRadius: 8,
      goalScale: GOAL["5v5"],
    },
    controlledIndex: attackingSeatIndex(FORMATION_5V5),
    aiDecisionInterval: 0.14,
    ai: AI_FUTSAL,
  },
  "6v6": {
    id: "6v6",
    label: "6v6",
    blurb: "Portero y 2-2-1. El escalon entre la sala y el futbol 7.",
    formation: FORMATION_6V6,
    physics: {
      speedScale: SPEED["6v6"],
      kickScale: KICK["6v6"],
      playerRadius: 15,
      ballRadius: 7.5,
      goalScale: GOAL["6v6"],
    },
    controlledIndex: attackingSeatIndex(FORMATION_6V6),
    aiDecisionInterval: 0.15,
    ai: AI_FUTSAL,
  },
  "7v7": {
    id: "7v7",
    label: "7v7",
    blurb: "Futbol 7 exacto: 2-3-1 con portero. Mas espacio real.",
    formation: FORMATION_7V7,
    physics: {
      speedScale: SPEED["7v7"],
      kickScale: KICK["7v7"],
      playerRadius: 14,
      ballRadius: 7,
      goalScale: GOAL["7v7"],
    },
    controlledIndex: attackingSeatIndex(FORMATION_7V7),
    aiDecisionInterval: 0.16,
    ai: AI_FUTSAL,
  },
  "8v8": {
    id: "8v8",
    label: "8v8",
    blurb: "Portero y 3-3-1. Ya se juega por dentro y por fuera.",
    formation: FORMATION_8V8,
    physics: {
      speedScale: SPEED["8v8"],
      kickScale: KICK["8v8"],
      playerRadius: 13,
      ballRadius: 6.6,
      goalScale: GOAL["8v8"],
    },
    controlledIndex: attackingSeatIndex(FORMATION_8V8),
    aiDecisionInterval: 0.17,
    ai: AI_ELEVEN,
  },
  "9v9": {
    id: "9v9",
    label: "9v9",
    blurb: "Portero y 3-3-2. Dos puntas y campo de verdad.",
    formation: FORMATION_9V9,
    physics: {
      speedScale: SPEED["9v9"],
      kickScale: KICK["9v9"],
      playerRadius: 12.5,
      ballRadius: 6.4,
      goalScale: GOAL["9v9"],
    },
    controlledIndex: attackingSeatIndex(FORMATION_9V9),
    aiDecisionInterval: 0.175,
    ai: AI_ELEVEN,
  },
  "10v10": {
    id: "10v10",
    label: "10v10",
    blurb: "Portero y 4-3-2. Casi el partido completo.",
    formation: FORMATION_10V10,
    physics: {
      speedScale: SPEED["10v10"],
      kickScale: KICK["10v10"],
      playerRadius: 11.5,
      ballRadius: 6.2,
      goalScale: GOAL["10v10"],
    },
    controlledIndex: attackingSeatIndex(FORMATION_10V10),
    aiDecisionInterval: 0.178,
    ai: AI_ELEVEN,
  },
  "11v11": {
    id: "11v11",
    label: "11v11",
    blurb: "Partido completo a medio gas. Posicion y paciencia.",
    formation: FORMATION_442,
    physics: {
      speedScale: SPEED["11v11"],
      kickScale: KICK["11v11"],
      playerRadius: 11,
      ballRadius: 6,
      goalScale: GOAL["11v11"],
    },
    controlledIndex: attackingSeatIndex(FORMATION_442),
    aiDecisionInterval: 0.18,
    ai: AI_ELEVEN,
  },
};

/** Modo activo del partido. Se cambia desde el menu antes de crear el match. */
export let ACTIVE_MATCH_MODE: MatchMode = MATCH_MODES["3v3"];

export function setMatchMode(id: MatchModeId): MatchMode {
  ACTIVE_MATCH_MODE = MATCH_MODES[id];
  return ACTIVE_MATCH_MODE;
}

export function playersPerTeam(): number {
  return ACTIVE_MATCH_MODE.formation.length;
}

/**
 * Jugadores de CAMPO por equipo (sin el portero).
 *
 * Es el numero que gobierna la tactica: cuanta gente hay para presionar,
 * apoyar y marcar. `playersPerTeam` incluye al portero desde que todos los
 * modos lo tienen, asi que los umbrales del tipo `n <= 3` se desplazaron un
 * jugador entero y el 4v4 empezo a comportarse como el antiguo 5v5. Toda la
 * heuristica de la IA debe usar ESTE.
 */
export function fieldPlayersPerTeam(): number {
  return ACTIVE_MATCH_MODE.formation.filter((spec) => spec.role !== "gk").length;
}

/** True si la formacion incluye un portero dedicado (p. ej. 11v11 legacy). */
export function modeHasGoalkeeper(mode: MatchMode = ACTIVE_MATCH_MODE): boolean {
  return mode.formation.some((spec) => spec.role === "gk");
}

/** Escala una constante de velocidad segun el modo activo. */
export function modeSpeed(base: number): number {
  return base * ACTIVE_MATCH_MODE.physics.speedScale;
}

/** Escala una constante de golpeo segun el modo activo. */
export function modeKick(base: number): number {
  return base * ACTIVE_MATCH_MODE.physics.kickScale;
}

/**
 * Boca de porteria del modo activo. Es una funcion y no una constante a
 * proposito: el modo se elige en el menu, despues de cargar los modulos, asi
 * que cualquier `const MOUTH = FIELD.GOAL_HEIGHT / 2` a nivel de modulo se
 * quedaria congelado con el valor del modo por defecto.
 */
/**
 * Altura del larguero en unidades de mundo.
 *
 * Hasta 2026-08-24 no existia: la deteccion de gol ignoraba `loft` por
 * completo y un balon a 200 de altura entraba igual que uno a ras de cesped.
 *
 * NO depende de `goalScale`, al reves que `goalMouth`. Ver `CROSSBAR_HEIGHT`:
 * el ancho se escala por equilibrio y la altura no puede seguirlo.
 */
export function crossbarHeight(): number {
  return FIELD.CROSSBAR_HEIGHT;
}

/**
 * Radio en pantalla al que se quiere ver a un jugador, en unidades de mundo.
 *
 * Antes el encuadre se anclaba al BALON (radio 13, el del 3v3). Salia casi
 * bien por casualidad —el balon mide mas o menos medio jugador en todos los
 * modos— pero con una deriva que iba justo al reves de lo que interesa: el
 * 2v2 pintaba las fichas a 25 y el 11v11 a 23,8. El modo con mas gente en el
 * campo era el que las enseñaba mas pequeñas.
 *
 * Anclarlo al jugador quita la deriva, y subir la cifra acerca la camara en
 * todos los modos: 33 son 1,32x mas cerca de lo que habia.
 */
const TARGET_PLAYER_ON_SCREEN = 33;

/**
 * Techo de acercamiento.
 *
 * No es matematica, es diseño: acercarse recorta campo, y por debajo de un
 * tercio de campo a la vista el radar deja de ser una ayuda y pasa a ser la
 * unica forma de jugar. Con 2,8 el 11v11 —el peor caso— conserva el 36%.
 *
 * El precio de este tope es que 10v10 y 11v11 no llegan al tamaño objetivo de
 * ficha; se quedan en 32,2 y 30,8. Es el intercambio elegido a proposito:
 * antes que ver la ficha enorme sin saber donde esta nadie.
 */
const ZOOM_MAX = 2.8;

/**
 * Acercamiento de camara del modo activo, sobre el encaje a pantalla.
 *
 * El campo mide lo mismo en todos los modos (1600x1000); lo que cambia es el
 * tamaño de las fichas. La regla es que **el jugador ocupe siempre lo mismo en
 * pantalla**, asi que el acercamiento es inversamente proporcional a su radio.
 *
 * A cambio, en los modos grandes deja de verse el campo entero. Es deliberado:
 * para eso esta el radar.
 */
export function modeZoom(mode: MatchMode = ACTIVE_MATCH_MODE): number {
  const raw = TARGET_PLAYER_ON_SCREEN / mode.physics.playerRadius;
  return Math.min(ZOOM_MAX, Math.max(1, raw));
}

export function goalMouth(): { half: number; top: number; bottom: number } {
  const half = (FIELD.GOAL_HEIGHT * ACTIVE_MATCH_MODE.physics.goalScale) / 2;
  const center = FIELD.HEIGHT / 2;
  return { half, top: center - half, bottom: center + half };
}
